import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "antd";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * The super-admin app had no error boundary anywhere: if any page threw
 * during render (e.g. calling `.map()` on a field the API omitted, a
 * malformed date, a null a component didn't expect), React unmounts the
 * whole tree and the browser shows a blank white screen with nothing a
 * platform admin can do except hard-refresh and hope the same crash
 * doesn't recur. This catches render-time errors anywhere below it and
 * shows a recoverable screen instead.
 *
 * Note: this only catches errors thrown during React's render/lifecycle —
 * it does NOT catch promise rejections from API calls (those are handled
 * per-page via react-query's isError/onError, where present).
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 px-6 text-center">
          <AlertTriangle size={40} className="text-amber-500 mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Something went wrong</h2>
          <p className="text-slate-500 text-sm mb-5 max-w-sm">
            This page hit an unexpected error. Reloading usually fixes it — if it keeps
            happening, please let engineering know.
          </p>
          <Button type="primary" style={{ background: "#4c1d95", borderColor: "#4c1d95" }} onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
